import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, ReplaySubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '@environments/environment';

export interface AiTokensUsage {
  aiTokensUsed: number;
  aiTokensLimit: number;
  aiTokensPurchased: number;
  remaining: number;
  aiTokensCapacity?: number;
  name?: string;
  email?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AiTokensUsageService {
  private apiUrl = `${environment.apiUrl}/ai-tokens-usage`;
  private tokensSubject = new ReplaySubject<AiTokensUsage>(1);
  public tokens$ = this.tokensSubject.asObservable();
  private currentTokens: AiTokensUsage = {
    aiTokensUsed: 0,
    aiTokensLimit: 0,
    aiTokensPurchased: 0,
    remaining: 0,
    aiTokensCapacity: 0
  };

  constructor(private http: HttpClient) {}

  /**
   * Get AI tokens usage for the current logged-in user
   */
  getMyUsage(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/my-usage`).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.currentTokens = {
            aiTokensUsed: response.data.aiTokensUsed || 0,
            aiTokensLimit: response.data.aiTokensLimit || 0,
            aiTokensPurchased: response.data.aiTokensPurchased || 0,
            remaining: response.data.remaining || 0,
            aiTokensCapacity: response.data.aiTokensCapacity || 0
          };
          this.tokensSubject.next(this.currentTokens);
        }
      })
    );
  }

  /**
   * Increment AI tokens usage (usually called automatically by backend)
   */
  incrementUsage(tokensUsed: number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/increment`, { tokensUsed }).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.currentTokens = {
            aiTokensUsed: response.data.aiTokensUsed || response.data.totalTokensUsed || 0,
            aiTokensLimit: (response.data.aiTokensLimit ?? response.data.aiTokensCapacity) ?? 0,
            aiTokensPurchased: response.data.aiTokensPurchased || 0,
            remaining: response.data.remaining || 0,
            aiTokensCapacity: response.data.aiTokensCapacity || 0
          };
          this.tokensSubject.next(this.currentTokens);
        }
      })
    );
  }

  /**
   * Get current tokens value from subject (synchronous)
   */
  getCurrentTokens(): AiTokensUsage {
    return this.currentTokens;
  }

  /**
   * Refresh tokens count
   */
  refreshTokens(): void {
    this.getMyUsage().subscribe();
  }
}
